import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { Skeleton } from "@/components/common/EmptyState";
import { money } from "@/lib/format";
import { Wallet, Users, Receipt, TrendingUp } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const tip = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 };

export default function FinanceDashboard() {
  const { data, isLoading } = useQuery({ queryKey: ["fin-dashboard"], queryFn: () => get("/finance/dashboard") });
  if (isLoading || !data) return <Skeleton className="h-96" />;
  const k = data.kpis;

  return (
    <div>
      <PageHeader title="Project Profitability" subtitle="Revenue, cost and margin overview." />
      <div className="grid grid-cols-4 gap-4">
        <KpiCard label="Revenue" value={money(k.revenue)} icon={Wallet} accent="green" />
        <KpiCard label="Labor Cost" value={money(k.labor)} icon={Users} accent="amber" />
        <KpiCard label="Expenses" value={money(k.expenses)} icon={Receipt} accent="violet" />
        <KpiCard label="Profit" value={money(k.profit)} icon={TrendingUp} accent={k.profit >= 0 ? "green" : "primary"} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Section title="Profit by Project" testid="fin-profit">
          <div className="p-4" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.by_project}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="project" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tip} />
                <Bar dataKey="profit" fill="#22C55E" radius={[6, 6, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section title="Cashflow" testid="fin-cashflow">
          <div className="p-4" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.cashflow}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tip} />
                <Line type="monotone" dataKey="inflow" stroke="#22C55E" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="outflow" stroke="#EF4444" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Section>
      </div>
    </div>
  );
}
